import React, { useState, useEffect } from 'react';
import { TextField, Grid, InputAdornment } from '@mui/material';
import { Person, Phone } from '@mui/icons-material';
import FormDialog from '../Common/FormDialog';
import api from '../../utils/api';
import { Broker } from '../../types';
import ToastNotification from '../../utils/toastNotification';

interface EditBrokerDialogProps {
  open: boolean;
  broker: Broker | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  name: string;
  phone: string;
  notes: string;
}

const EditBrokerDialog: React.FC<EditBrokerDialogProps> = ({
  open,
  broker,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>({ name: '', phone: '', notes: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (broker) {
      setFormData({
        name: broker.name || '',
        phone: broker.phone || '',
        notes: broker.notes || '',
      });
      setErrors({});
    }
  }, [broker]);

  const handleChange = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || !broker) return;
    try {
      setIsSubmitting(true);
      await api.put(`/brokers/${broker.id}`, {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        notes: formData.notes.trim() || undefined,
      });
      ToastNotification.success('Broker updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating broker:', error);
      ToastNotification.error('Failed to update broker');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title="Edit Broker"
      subtitle={broker?.name}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitText="Save Changes"
      isSubmitting={isSubmitting}
    >
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Person /></InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Phone"
            value={formData.phone}
            onChange={handleChange('phone')}
            error={!!errors.phone}
            helperText={errors.phone}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Phone /></InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes"
            value={formData.notes}
            onChange={handleChange('notes')}
          />
        </Grid>
      </Grid>
    </FormDialog>
  );
};

export default EditBrokerDialog;